<script setup lang="ts">
import { useStudentStore } from '@/stores/student'
import { useUpcomingInterviewsQuery } from '@/composable/queries/Dara/interview'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(timezone)

const studentStore = useStudentStore()

const studentId = computed(() => studentStore.id)
const isInterviewQueryEnabled = computed(() => !!studentStore.id)

const {
  data: interviewData,
  isPending: isInterviewPending,
} = useUpcomingInterviewsQuery(studentId, isInterviewQueryEnabled)

const upcomingInterviewStart = ref<string | null>(null)
const timerSecondsLeft = ref<number>(0)
let countdownInterval: ReturnType<typeof setTimeout>

const formattedHours = computed(() => String(Math.floor(dayjs.duration(timerSecondsLeft.value, 'seconds').asHours())).padStart(2, '0'))
const formattedMinutes = computed(() => String(dayjs.duration(timerSecondsLeft.value, 'seconds').minutes()).padStart(2, '0'))
const formattedSeconds = computed(() => String(dayjs.duration(timerSecondsLeft.value, 'seconds').seconds()).padStart(2, '0'))

function initializeCountdown() {
  if (interviewData.value?.length) {
    upcomingInterviewStart.value = interviewData.value[0].start_at
    const start = dayjs.utc(upcomingInterviewStart.value).tz(studentStore.user.timezone || dayjs.tz.guess())
    const now = dayjs()
    const diffInSeconds = Math.max(start.diff(now, 'second'), 0)
    timerSecondsLeft.value = diffInSeconds

    if (countdownInterval) clearInterval(countdownInterval)
    countdownInterval = setInterval(() => {
      if (timerSecondsLeft.value > 0) {
        timerSecondsLeft.value--
      } else {
        clearInterval(countdownInterval)
      }
    }, 1000)
  }
}

watch(interviewData, () => {
  initializeCountdown()
})

onMounted(() => {
  initializeCountdown()
})

onUnmounted(() => {
  if (countdownInterval) clearInterval(countdownInterval)
})
</script>

<template>
  <VCard v-if="!isInterviewPending && interviewData?.length" class="dashboard-card dashboard-card--small dashboard-card--interview h-100 d-flex flex-column">
    <VCardText class="d-flex flex-column justify-space-between pa-2">
      <p class="dashboard-card--interview__title text-center font-weight-medium text-branding-grey">
        {{ $t('pages.dashboard.interview.title') }}
      </p>
      <div class="dashboard-card--interview__counter text-center mb-2">
        <div class="d-inline-block">
          <span v-for="(figure, index) in formattedHours.split('')" :key="'hours-' + index" class="dashboard-card--interview__figure bg-grey-100 text-brand-mycareerchoices" :class="{ 'mr-1': index === 0 }">
            {{ figure }}
          </span>
        </div>
        <span class="dashboard-card--interview__divider text-brand-mycareerchoices">:</span>
        <div class="d-inline-block">
          <span v-for="(figure, index) in formattedMinutes.split('')" :key="'minutes-' + index" class="dashboard-card--interview__figure bg-grey-100 text-brand-mycareerchoices" :class="{ 'mr-1': index === 0 }">
            {{ figure }}
          </span>
        </div>
        <span class="dashboard-card--interview__divider text-brand-mycareerchoices">:</span>
        <div class="d-inline-block">
          <span v-for="(figure, index) in formattedSeconds.split('')" :key="'seconds-' + index" class="dashboard-card--interview__figure bg-grey-100 text-brand-mycareerchoices" :class="{ 'mr-1': index === 0 }">
            {{ figure }}
          </span>
        </div>
      </div>
      <VBtn :to="`/interviews/${interviewData[0].id}`" variant="tonal" rounded="xl" class="dashboard-card--interview__join-btn w-100">
        {{ $t('pages.dashboard.interview.join_btn') }}
      </VBtn>
    </VCardText>
  </VCard>
</template>
